import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { type DietaryProperty } from '@sg/graphql-schema';

import { type DietaryRestrictionsTypes } from '../../machines';
import { getProductDietaryRestrictionsNames } from '../../utils';

/**
 * Returns a function that generates the "Contains {restrictions}" label based on provided restrictions.
 */
export const useDietaryRestrictions = (params: Params) => {
  const { restrictions } = params;

  const { formatMessage } = useIntl();

  return useCallback(
    (dietaryProperties: readonly DietaryProperty[] | undefined) => {
      const activeDietaryRestrictions = getProductDietaryRestrictionsNames(
        restrictions,
        dietaryProperties,
      );

      if (!activeDietaryRestrictions) return;

      return formatMessage(messages.containsRestrictions, {
        restrictions: activeDietaryRestrictions,
      });
    },
    [restrictions, formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  containsRestrictions: {
    defaultMessage: 'Contains {restrictions}',
    description: 'Dietary Restrictions | Contains restrictions',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  restrictions: DietaryRestrictionsTypes;
};
